import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import BrandLockup from "@/components/BrandLockup";
import { useAuth } from "@/contexts/AuthContext";
import { clearAuthAttempt, getAuthAttemptStatus } from "@/lib/authAttempt";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { normalizeReturnPath } from "@/lib/authRedirect";
import { supabase } from "@/lib/supabase";

function errorMessage(error: unknown): string {
  return friendlyErrorMessage(error, "sign-in", "That link couldn’t sign you in. Request a new sign-in email and try again.");
}

export default function AuthCallback() {
  const [, navigate] = useLocation();
  const { session, isLoading, isConfigured, error: authError } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const returnPath = normalizeReturnPath(params.get("next"));
  const attemptId = params.get("attempt");
  const authCode = params.get("code");
  const tokenHash = params.get("token_hash");
  const tokenType = params.get("type");
  const hasAuthPayload = Boolean(authCode || tokenHash);
  const attemptStatus = getAuthAttemptStatus(attemptId);
  const urlError = params.get("error_description") || params.get("error");
  const [showFailure, setShowFailure] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const verificationStarted = useRef(false);

  const isDifferentBrowser =
    (attemptStatus === "different-browser" || attemptStatus === "missing") && !urlError && !authError;
  const isExpiredAttempt = attemptStatus === "expired";
  const unsupportedTokenType = Boolean(tokenHash && tokenType && tokenType !== "email");

  useEffect(() => {
    if (
      !isConfigured ||
      !supabase ||
      !hasAuthPayload ||
      (tokenHash && tokenType && tokenType !== "email") ||
      !attemptId ||
      attemptStatus !== "matched" ||
      verificationStarted.current
    ) {
      return;
    }

    verificationStarted.current = true;
    const verification = tokenHash
      ? supabase.auth.verifyOtp({ token_hash: tokenHash, type: "email" })
      : supabase.auth.exchangeCodeForSession(authCode!);
    void verification
      .then(({ data, error }) => {
        if (error) throw error;
        if (!data.session) throw new Error("The confirmation link did not create a sign-in session.");
        clearAuthAttempt(attemptId);
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("token_hash");
        cleanUrl.searchParams.delete("type");
        cleanUrl.searchParams.delete("code");
        window.history.replaceState({}, "", cleanUrl.toString());
        navigate(returnPath, { replace: true });
      })
      .catch((error: unknown) => {
        setVerificationError(errorMessage(error));
        setShowFailure(true);
      });
  }, [attemptId, attemptStatus, isConfigured, navigate, returnPath, tokenHash, tokenType]);

  useEffect(() => {
    if (!isConfigured || isLoading || hasAuthPayload || !attemptId || attemptStatus !== "matched") return;
    if (session) {
      clearAuthAttempt(attemptId);
      navigate(returnPath, { replace: true });
      return;
    }
    const timer = window.setTimeout(() => setShowFailure(true), 700);
    return () => window.clearTimeout(timer);
  }, [attemptId, attemptStatus, hasAuthPayload, isConfigured, isLoading, navigate, returnPath, session]);

  const failure = verificationError || urlError || authError ||
    (unsupportedTokenType ? "This email link can’t be used. Please request a new six-digit sign-in code." : null);
  const shouldShowFailure =
    isConfigured &&
    (isDifferentBrowser ||
      isExpiredAttempt ||
      (!isLoading && !session && (showFailure || Boolean(failure))));

  return (
    <div className="paper-shell inner-shell">
      <header className="site-header">
        <BrandLockup />
      </header>
      <main className="callback-layout" aria-live="polite">
        {shouldShowFailure ? (
          <section className="form-card callback-card">
            <p className="eyebrow"><span className="eyebrow-rule" /> Sign-in help</p>
            <h1>{isDifferentBrowser ? <>Use your code<br />instead<span className="title-period">.</span></> : <>Let’s try<br />that again<span className="title-period">.</span></>}</h1>
            <p className="form-description">
              {isDifferentBrowser
                ? "This link only works in the browser where you asked for it. Go back to that page and enter the 6-digit code from your email."
                : isExpiredAttempt
                  ? "This older sign-in link has expired. Request a new six-digit code and enter it on the sign-in page."
                  : failure || "We couldn’t finish sign-in with that email link. Request a fresh six-digit code and enter it on the sign-in page."}
            </p>
            <Link href={`/login?next=${encodeURIComponent(returnPath)}`} className="primary-action full-width">
              Send a new code <span aria-hidden="true">→</span>
            </Link>
          </section>
        ) : (
          <section className="callback-card">
            <span className="loading-mark" aria-hidden="true" />
            <p className="eyebrow">Home, Twice</p>
            <h1>Opening your<br />journal<span className="title-period">.</span></h1>
            <p className="form-description">
              {!isConfigured
                ? "Supabase settings are needed to complete sign-in."
                : hasAuthPayload
                  ? "Confirming your email and opening your journal."
                  : "One moment while we finish signing you in."}
            </p>
          </section>
        )}
      </main>
      <footer className="site-footer">
        <nav className="legal-footer-links" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <Link href="/terms">Terms</Link>
        </nav>
        <span>Published by Kwaderno &amp; Co.</span>
      </footer>
    </div>
  );
}
